import { useEffect, useState } from 'react';
import { featureFlagsService } from '@/lib/services/featureFlagsService';

interface AuthFeatureFlags {
  showAdsConsent: boolean;
  showGoogleAuth: boolean;
  showVkAuth: boolean;
  showForYouTab: boolean;
}

const initialFlags: AuthFeatureFlags = {
  showAdsConsent: true, // Default to true until flags load
  showGoogleAuth: true, // Show by default to avoid hiding Google button when flags/caches are stale
  showVkAuth: false, // Hidden by default - only show when explicitly enabled via feature flag
  showForYouTab: false, // Hidden by default - only show when explicitly enabled via feature flag
};

// Clear stale cache on module load to ensure fresh flags
try {
  const cacheKey = 'feature_flags_cache';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    const parsed = JSON.parse(cached);
    const cacheAge = Date.now() - parsed.timestamp;
    // If cache is older than 5 minutes, clear it for fresh data
    if (cacheAge > 5 * 60 * 1000) {
      localStorage.removeItem(cacheKey);
    }
  }
} catch {
  // Ignore errors
}

export function useAuthFeatureFlags() {
  const [flags, setFlags] = useState<AuthFeatureFlags>(initialFlags);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadFlags = async () => {
      try {
        // Force-refresh to avoid 30min cache hiding critical auth UI (e.g., Google button)
        const rawFlags = await featureFlagsService.refreshFlags();

        const adsConsent = (rawFlags['signup.show_ads_consent'] ?? 'true') === 'true';
        const googleAuth = (rawFlags['signup.show_google_auth'] ?? 'true') === 'true';
        const vkAuth = (rawFlags['auth_vk_enabled'] ?? 'false') === 'true';
        const forYouTab = (rawFlags['tabbar.show_for_you'] ?? 'false') === 'true';

        if (!isActive) return;
        setFlags({ 
          showAdsConsent: adsConsent, 
          showGoogleAuth: googleAuth, 
          showVkAuth: vkAuth,
          showForYouTab: forYouTab,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadFlags();

    return () => {
      isActive = false;
    };
  }, []);

  return { ...flags, isLoading };
}
